/**
 * /downhole/components/:id — one tool and its history (fieldiq-api#882
 * `GET /downhole/components/:id`).
 *
 * WRITES (api#900): Edit, for the API's inventory roles (admin, office_staff,
 * dispatcher) — specs, serial, category, and the one status a person sets by
 * hand, available ↔ retired. Print QR is a read and is open to everyone who can
 * open the page.
 *
 * LOG INSPECTION and OPEN WORK ORDER (api#905, WorkOrderDialogs) are the
 * build roles' (the API's DH_BUILD), and only while the tool is in the shop —
 * available, maintenance or failed. On any other status the API answers 409
 * COMPONENT_NOT_IN_SHOP, so the buttons are not drawn. The office roles pick
 * an assignee as they open it; anyone else's order opens unassigned.
 *
 * WEAR IS USED, NOT REMAINING. The unrouted screen bound the API's wear figure
 * to a "Wear Remaining" tile with a green-when-high ramp — the opposite fact.
 * The tiles here read "Wear used" and print the API's two figures (hours and
 * runs) as returned; a component with no limit reads `—`, not 0%.
 *
 * FIELDS THE OLD SCREEN NAMED THAT DO NOT EXIST, and what replaced them:
 *   total_depth_m   → no such figure. `total_footage_ft` is cumulative FOOTAGE
 *                     DRILLED — a different fact — so it is labelled Footage.
 *   run.job         → no job on a run. Well / Rig, which is what a run records.
 *   connection_type → vestigial (NULL in every row); top and bottom are shown.
 *
 * Units through `formatDh` and the tenant's unit system; instants through the
 * tenant's time zone.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { ClipboardCheck, Gauge, Hash, Loader2, Pencil, QrCode, Ruler, Timer, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeComponent } from '../../hooks/useDownhole';
import { formatInstantDay, formatInstantTenantDay } from '../../lib/calendar-date';
import {
  DH_ASSEMBLY_STATUS_SKIN,
  DH_CATEGORY_LABEL,
  DH_COMPONENT_STATUS_SKIN,
  DH_CONDITION_LABEL,
  DH_DISASSEMBLY_REASON_LABEL,
  DH_INSPECTION_RESULT_SKIN,
  DH_PRIORITY_LABEL,
  DH_RECOMMEND_ACTION_LABEL,
  DH_WORK_ORDER_STATUS_SKIN,
  DH_WORK_TYPE_LABEL,
  dhLabel,
  dhLabelOrText,
} from '../../lib/downhole-labels';
import { canAssignDownholeWork, canBuildDownhole, canManageDownholeInventory } from '../../lib/downhole-roles';
import { formatDh, formatWearPercent } from '../../lib/downhole-units';
import { fmtHours, fmtQty } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { DhUnitSystem } from '../../types/downhole';
import { ComponentFormDialog, QrLabelDialog } from './ComponentDialogs';
import { DhBadge, DhCategoryPill, DhEmpty, DhPanel, DhSpec, DhWearMeter } from './DownholeBits';
import { OpenWorkOrderDialog, isInShop } from './WorkOrderDialogs';

/** A secondary action on the band: white/25 edge and no colour class, so it keeps the band's ink in both themes. */
const BAND_SECONDARY = 'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10';

const TH = 'px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const TD = 'px-4 py-3 text-sm text-fieldiq-text';
const TD_NUM = 'px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text';

/** "Well / Rig", either side `—` when absent, one `—` when both are. */
function wellRig(well: string | null | undefined, rig: string | null | undefined): string {
  if (!well && !rig) return '—';
  return `${well || '—'} / ${rig || '—'}`;
}

/**
 * last_inspection_result is free varchar. A known value gets its hue; anything
 * else is printed as written on slate — an unrecognised result is not a Pass.
 */
function InspectionResult({ value }: { value: string | null }) {
  if (!value) return <>—</>;
  const known = Object.prototype.hasOwnProperty.call(DH_INSPECTION_RESULT_SKIN, value.toLowerCase());
  if (known) return <DhBadge map={DH_INSPECTION_RESULT_SKIN} value={value.toLowerCase()} />;
  return (
    <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none text-fi-signal-ink">
      {value}
    </span>
  );
}

export default function ComponentDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageDownholeInventory(user?.role);
  const [editing, setEditing] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [opening, setOpening] = useState<'inspection' | 'work_order' | null>(null);
  const query = useDownholeComponent(id);
  const failure = queryLoadFailure(query);
  const comp = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units: DhUnitSystem | null = meta?.unit_system ?? null;
  const tz = meta?.time_zone ?? null;
  const day = (v: string | null | undefined) => formatInstantDay(v, tz) ?? '—';
  const canOpenWork = canBuildDownhole(user?.role) && isInShop(comp?.status);

  const loading = query.isLoading;

  return (
    <div data-testid="downhole-component-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={comp ? dhLabel(DH_CATEGORY_LABEL, comp.category) : 'Component'}
        title={comp?.serial_number ?? (loading ? 'Loading…' : 'Component')}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          comp ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={BAND_SECONDARY} onClick={() => setQrOpen(true)}>
                <QrCode className="size-4" aria-hidden="true" />
                Print QR
              </button>
              {canOpenWork && (
                <>
                  <button type="button" className={BAND_SECONDARY} onClick={() => setOpening('inspection')}>
                    <ClipboardCheck className="size-4" aria-hidden="true" />
                    Log inspection
                  </button>
                  <button type="button" className={BAND_SECONDARY} onClick={() => setOpening('work_order')}>
                    <Wrench className="size-4" aria-hidden="true" />
                    Open work order
                  </button>
                </>
              )}
              {canManage && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => setEditing(true)}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={
          failure || (!loading && !comp)
            ? undefined
            : [
                {
                  icon: <Hash className="size-4" aria-hidden="true" />,
                  label: 'Runs',
                  sub: 'Lifetime',
                  tone: 'neutral',
                  loading,
                  value: fmtQty(comp?.total_runs),
                },
                {
                  icon: <Timer className="size-4" aria-hidden="true" />,
                  label: 'Run hours',
                  sub: 'Lifetime',
                  tone: 'info',
                  loading,
                  value: fmtHours(comp?.total_run_hours, 1),
                },
                {
                  icon: <Ruler className="size-4" aria-hidden="true" />,
                  label: 'Footage',
                  sub: 'Drilled',
                  tone: 'neutral',
                  loading,
                  value: formatDh(comp?.total_footage_ft, 'length', units, { digits: 0 }),
                },
                {
                  icon: <Gauge className="size-4" aria-hidden="true" />,
                  label: 'Wear used',
                  sub: 'Of the hours limit',
                  tone: 'accent',
                  loading,
                  value: formatWearPercent(comp?.wear_percent_hours),
                },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading component" />
            </div>
          ) : failure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this component" />
            </div>
          ) : !comp ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <DhEmpty>This component does not exist in your register.</DhEmpty>
            </div>
          ) : (
            <>
              <div className="grid gap-4 xl:grid-cols-2">
                <DhPanel eyebrow="Specification" title={comp.description || 'Component'}>
                  <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
                    <DhBadge map={DH_COMPONENT_STATUS_SKIN} value={comp.status} />
                    <DhCategoryPill category={comp.category} />
                    {!comp.is_active && (
                      <span className="text-xs font-bold text-fieldiq-muted">Inactive — cannot be added to an assembly</span>
                    )}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3">
                    <DhSpec label="OD" mono value={formatDh(comp.od, 'diameter', units)} />
                    <DhSpec label="ID" mono value={formatDh(comp.id_inner, 'diameter', units)} />
                    <DhSpec label="Length" mono value={formatDh(comp.length_ft, 'length', units, { digits: units === 'metric' ? 2 : 1 })} />
                    <DhSpec label="Weight" mono value={formatDh(comp.weight_lbs, 'weight', units)} />
                    <DhSpec label="Connection top" value={displayValue(comp.connection_top)} />
                    <DhSpec label="Connection bottom" value={displayValue(comp.connection_bottom)} />
                    <DhSpec label="Fish neck OD" mono value={formatDh(comp.fish_neck_od, 'diameter', units)} />
                    <DhSpec label="Max temperature" mono value={formatDh(comp.max_temp_f, 'temperature', units)} />
                    <DhSpec label="Max WOB" mono value={formatDh(comp.max_wob_klbs, 'force', units)} />
                    <DhSpec label="Pressure rating" mono value={formatDh(comp.pressure_rating, 'pressure', units)} />
                    <DhSpec label="Manufacturer" value={displayValue(comp.manufacturer)} />
                    <DhSpec label="Model" value={displayValue(comp.model)} />
                    <DhSpec label="Year built" value={displayValue(comp.year_manufactured)} />
                    <DhSpec label="Location" value={displayValue(comp.location)} />
                  </dl>
                </DhPanel>

                <DhPanel eyebrow="Condition" title="Wear and inspection">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 sm:grid-cols-3">
                    <DhSpec label="Wear used — hours" value={<DhWearMeter percent={comp.wear_percent_hours} label="Wear used, of the hours limit" />} />
                    <DhSpec label="Wear used — runs" value={<DhWearMeter percent={comp.wear_percent_runs} label="Wear used, of the runs limit" />} />
                    <span className="hidden sm:block" />
                    <DhSpec label="Hours limit" mono value={fmtHours(comp.wear_limit_hours, 1)} />
                    <DhSpec label="Runs limit" mono value={fmtQty(comp.wear_limit_runs)} />
                    <span className="hidden sm:block" />
                    <DhSpec label="Last inspection" value={day(comp.last_inspection)} />
                    <DhSpec label="Result" value={<InspectionResult value={comp.last_inspection_result} />} />
                    <DhSpec label="Next inspection due" value={day(comp.next_inspection_due)} />
                    <DhSpec label="Box thread" value={displayValue(comp.box_thread_condition)} />
                    <DhSpec label="Pin thread" value={displayValue(comp.pin_thread_condition)} />
                  </dl>
                  {comp.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{comp.notes}</p>}
                </DhPanel>
              </div>

              <DhPanel eyebrow="History" title="Runs" footer={comp.run_history.length ? `${comp.run_history.length} runs while installed` : undefined}>
                {comp.run_history.length === 0 ? (
                  <DhEmpty>This component has no runs on record.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px]">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr>
                          <th scope="col" className={TH}>Assembly</th>
                          <th scope="col" className={TH}>Run</th>
                          <th scope="col" className={TH}>Well / Rig</th>
                          <th scope="col" className={TH}>Run in</th>
                          <th scope="col" className={TH}>Run out</th>
                          <th scope="col" className={`${TH} text-right`}>Depth out</th>
                          <th scope="col" className={`${TH} text-right`}>Footage</th>
                          <th scope="col" className={`${TH} text-right`}>Circ. hrs</th>
                          <th scope="col" className={`${TH} text-right`}>Rot. hrs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.run_history.map((r) => (
                          <tr key={r.id} className="border-t border-fieldiq-border">
                            <td className={`${TD} font-mono`}>{r.assembly_number || '—'}</td>
                            <td className={TD}>
                              <Link to={`/downhole/run-logs/${r.id}`} className="text-fieldiq-accent-signal hover:underline">
                                {displayValue(r.run_number)}
                              </Link>
                              {r.lost_in_hole_here && (
                                <span className="ml-2 rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                                  Lost in hole
                                </span>
                              )}
                            </td>
                            <td className={TD}>{wellRig(r.well_name, r.rig_name)}</td>
                            <td className={TD}>{day(r.run_in_at)}</td>
                            <td className={TD}>{day(r.run_out_at)}</td>
                            <td className={TD_NUM}>{formatDh(r.depth_out_ft, 'length', units, { digits: 0 })}</td>
                            <td className={TD_NUM}>{formatDh(r.footage_drilled, 'length', units, { digits: 0 })}</td>
                            <td className={TD_NUM}>{fmtHours(r.circulating_hours, 1)}</td>
                            <td className={TD_NUM}>{fmtHours(r.rotating_hours, 1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <DhPanel eyebrow="History" title="Assemblies">
                {comp.assembly_history.length === 0 ? (
                  <DhEmpty>This component has never been made up into an assembly.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr>
                          <th scope="col" className={TH}>Assembly</th>
                          <th scope="col" className={TH}>Position</th>
                          <th scope="col" className={TH}>Well / Rig</th>
                          <th scope="col" className={TH}>Installed</th>
                          <th scope="col" className={TH}>Removed</th>
                          <th scope="col" className={TH}>Condition on return</th>
                          <th scope="col" className={TH}>Assembly now</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.assembly_history.map((a) => (
                          <tr key={`${a.assembly_id}-${a.installed_at}`} className="border-t border-fieldiq-border">
                            <td className={TD}>
                              <Link to={`/downhole/assemblies/${a.assembly_id}/build`} className="font-mono text-fieldiq-accent-signal hover:underline">
                                {a.assembly_number || '—'}
                              </Link>
                              {a.assembly_name && <span className="ml-2 text-fieldiq-muted">{a.assembly_name}</span>}
                            </td>
                            <td className={TD}>{displayValue(a.position)}</td>
                            <td className={TD}>{wellRig(a.well_name, a.rig_name)}</td>
                            <td className={TD}>{day(a.installed_at)}</td>
                            <td className={TD}>{a.removed_at ? day(a.removed_at) : 'Still installed'}</td>
                            <td className={TD}>{dhLabelOrText(DH_CONDITION_LABEL, a.condition_on_return)}</td>
                            {/* The assembly's CURRENT status, not its status during this membership. */}
                            <td className={TD}><DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={a.assembly_status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <DhPanel eyebrow="Shop" title="Work orders">
                {comp.work_orders.length === 0 ? (
                  <DhEmpty>No work orders have been raised against this component.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px]">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr>
                          <th scope="col" className={TH}>WO #</th>
                          <th scope="col" className={TH}>Type</th>
                          <th scope="col" className={TH}>Status</th>
                          <th scope="col" className={TH}>Priority</th>
                          <th scope="col" className={TH}>Description</th>
                          <th scope="col" className={TH}>Assigned to</th>
                          <th scope="col" className={TH}>Opened</th>
                          <th scope="col" className={TH}>Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.work_orders.map((w) => (
                          <tr key={w.id} className="border-t border-fieldiq-border">
                            <td className={`${TD} whitespace-nowrap font-mono`}>
                              <Link to={`/downhole/work-orders/${w.id}`} className="text-fieldiq-accent-signal hover:underline">
                                {w.wo_number || '—'}
                              </Link>
                            </td>
                            <td className={TD}>{dhLabel(DH_WORK_TYPE_LABEL, w.work_type)}</td>
                            <td className={TD}><DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={w.status} /></td>
                            <td className={TD}>{dhLabel(DH_PRIORITY_LABEL, w.priority)}</td>
                            <td className={`${TD} max-w-[24rem]`}>{displayValue(w.description)}</td>
                            <td className={TD}>{displayValue(w.assigned_to_name)}</td>
                            <td className={TD}>{day(w.created_at)}</td>
                            {/* due_date is a calendar day, not an instant. */}
                            <td className={TD}>{formatInstantTenantDay(w.due_date, tz) ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <DhPanel eyebrow="Shop" title="Teardown history">
                {comp.disassembly_history.length === 0 ? (
                  <DhEmpty>This component has not been through a teardown.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                        <tr>
                          <th scope="col" className={TH}>Assembly</th>
                          <th scope="col" className={TH}>Torn down</th>
                          <th scope="col" className={TH}>Reason</th>
                          <th scope="col" className={TH}>Condition</th>
                          <th scope="col" className={TH}>Recommended</th>
                          <th scope="col" className={`${TH} text-right`}>OD measured</th>
                          <th scope="col" className={TH}>Wear notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comp.disassembly_history.map((d) => (
                          <tr key={d.disassembly_id} className="border-t border-fieldiq-border">
                            <td className={`${TD} font-mono`}>{d.assembly_number || '—'}</td>
                            <td className={TD}>{day(d.disassembled_at)}</td>
                            <td className={TD}>{dhLabel(DH_DISASSEMBLY_REASON_LABEL, d.reason)}</td>
                            <td className={TD}>{dhLabel(DH_CONDITION_LABEL, d.condition)}</td>
                            <td className={TD}>{dhLabel(DH_RECOMMEND_ACTION_LABEL, d.recommend_action)}</td>
                            <td className={TD_NUM}>{formatDh(d.od_measurement, 'diameter', units)}</td>
                            <td className={`${TD} max-w-[24rem]`}>{displayValue(d.wear_notes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>
            </>
          )}
        </div>
      </section>

      {comp && (
        <>
          <QrLabelDialog open={qrOpen} onClose={() => setQrOpen(false)} componentId={comp.id} units={units} />
          {canManage && <ComponentFormDialog open={editing} onClose={() => setEditing(false)} units={units} tz={tz} component={comp} />}
          {canOpenWork && opening && (
            <OpenWorkOrderDialog
              key={opening}
              open
              onClose={() => setOpening(null)}
              kind={opening}
              component={comp}
              canAssign={canAssignDownholeWork(user?.role)}
            />
          )}
        </>
      )}
    </div>
  );
}
